import { SalesInviteCard } from '@/pages/sales-team/SalesInviteCard';
import { SalesNotificationPreferences } from '@/pages/sales-team/SalesNotificationPreferences';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchMyAccount, updateMyName, updateMyPhone } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { InlineEditRow } from '@/components/profile/InlineEditRow';
import styles from './SuperAdminProfilePage.module.css';

/**
 * Phase 7g.4c Commit C — operator-tier profile editing surface.
 *
 * Lives at /profile. Renders for every non-SP authenticated user:
 * super_admin, tenant_admin, operator, viewer, third_party_admin,
 * fleet_admin. SPs hit the /profile → /me redirect at App.tsx and
 * land on SalesPartnerProfilePage instead.
 *
 * Shape mirrors SP Profile minus the SP-specific bits (no Connect
 * banner, no agent fetch). Two editable rows (Name, Phone) plus a
 * read-only Email + Role card below. The Role row is genuinely
 * useful in browser sessions that bounce between test accounts —
 * the user sees which persona is currently logged in without
 * having to inspect the dropdown.
 *
 * Reuses the shared InlineEditRow component for the row state
 * machine. Side effects (auth store sync on name, local state on
 * phone) live in the per-row onSave callbacks below; the
 * component itself stays presentation-only.
 *
 * Phone lives in component-local state because PublicUser does
 * not carry phone (only name is on the auth store snapshot).
 * Seeded from GET /me/account on mount; updated from the
 * /me/phone response on each successful save.
 */

const E164_RE = /^\+[1-9]\d{1,14}$/;

function validateName(value: string): string | null {
  if (value.trim().length === 0) return 'Name cannot be empty.';
  if (value.trim().length > 200) return 'Name must be 200 characters or fewer.';
  return null;
}

function validatePhone(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return 'Phone is required.';
  if (!E164_RE.test(trimmed)) {
    return 'Phone must be in international format, like +14155552671.';
  }
  return null;
}

export function SuperAdminProfilePage() {
  useRecentRoute('Profile');
  const user = useAuthStore((s) => s.user);
  const updateUser = useAuthStore((s) => s.updateUser);

  const accountQ = useQuery({
    queryKey: ['operator-profile', 'account'],
    queryFn: fetchMyAccount,
    staleTime: 60_000,
  });

  // Phone lives in component-local state. Seeds from the
  // /me/account fetch and updates after every successful PATCH.
  // Re-seeds on background refetch when the server-side value
  // diverges; the InlineEditRow internally guards against stomping
  // a mid-edit draft.
  const [localPhone, setLocalPhone] = useState('');
  useEffect(() => {
    if (accountQ.data && accountQ.data.phone !== null) {
      setLocalPhone(accountQ.data.phone);
    }
  }, [accountQ.data]);

  const handleSaveName = async (newName: string) => {
    await updateMyName(newName);
    // Optimistic auth-store sync. PublicUser carries name so the
    // TopNav UserChip updates immediately without a /auth/me
    // refetch.
    updateUser({ name: newName });
  };

  const handleSavePhone = async (newPhone: string) => {
    const updated = await updateMyPhone(newPhone);
    // Local state only. Phone is not on PublicUser; no auth-store
    // sync needed (and no consumer outside this page would notice
    // a missing sync).
    setLocalPhone(updated.phone ?? '');
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>My Profile</h1>
        <p className={styles.subtitle}>Your account contact details.</p>
      </header>
      {user && ['sales_rep','sales_manager'].includes(user.template ?? '') && <><SalesInviteCard /><SalesNotificationPreferences /></>}

      <div className={styles.fieldGroup}>
        <InlineEditRow
          label="Name"
          value={user?.name ?? ''}
          validator={validateName}
          onSave={handleSaveName}
          maxLength={200}
          emptyText="(not set)"
        />
        <InlineEditRow
          label="Phone"
          value={localPhone}
          inputType="tel"
          placeholder="+14155552671"
          validator={validatePhone}
          onSave={handleSavePhone}
          emptyText="(not set)"
          inputAriaLabel="Phone (international format)"
        />
      </div>

      <div className={styles.identityBlock}>
        <p className={styles.identityRow}>
          <span className={styles.identityLabel}>Email</span>
          <span className={styles.identityValue}>{user?.email ?? '—'}</span>
        </p>
        <p className={styles.identityRow}>
          <span className={styles.identityLabel}>Role</span>
          <span className={styles.identityValue}>
            <code className={styles.roleCode}>{user?.role ?? '—'}</code>
          </span>
        </p>
        <p className={styles.identityHint}>
          Email and role cannot be changed here. <a href="mailto:support@evinitiative.com">Contact support at support@evinitiative.com</a> to update either.
        </p>
      </div>
    </div>
  );
}
